/**
 * Combined-balance hook: sums every account balance (FX-converted to EUR)
 * plus every holding's market value (already EUR on the server).
 *
 * Same numbers the Imports hero shows under "Combined balance" — extracted
 * so the Trajectory planner can use it as the starting principal.
 */

import { useEffect, useState } from 'react';
import { getClient } from '@/lib/api';
import { useFxRates, type FxRates } from '@/lib/fx';
import type { components } from '@/lib/api.types';

type StoredBalance = components['schemas']['StoredBalanceOut'];
type Holding = components['schemas']['HoldingOut'];

export type CombinedBalance = {
  loading: boolean;
  error: string | null;
  totalEur: number;
  accountsEur: number;
  holdingsEur: number;
  capturedAt: string | null;
  hasData: boolean;
};

function toEur(amount: number, currency: string, rates: FxRates | null): number {
  if (currency === 'EUR') return amount;
  if (!rates) return amount;
  const rate = rates[currency.toUpperCase()];
  if (!rate || rate <= 0) return amount;
  return amount / rate;
}

export function useCombinedBalance(): CombinedBalance {
  const { rates } = useFxRates();
  const [balances, setBalances] = useState<StoredBalance[] | null>(null);
  const [holdings, setHoldings] = useState<Holding[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const c = getClient();
    if (!c) return;
    void (async () => {
      // Trigger a price refresh so the holdings total isn't stale; the
      // backend rate-limits this internally so it's cheap to hammer.
      await c.POST('/api/holdings/refresh-prices', {});
      if (cancelled) return;
      const [bRes, hRes] = await Promise.all([
        c.GET('/api/balances/latest'),
        c.GET('/api/holdings'),
      ]);
      if (cancelled) return;
      if (bRes.error) setError(JSON.stringify(bRes.error));
      else setBalances(bRes.data?.balances ?? []);
      if (!hRes.error) setHoldings(hRes.data?.holdings ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const accountsEur = (balances ?? []).reduce(
    (acc, b) => acc + toEur(Number(b.amount), b.currency, rates),
    0,
  );
  const holdingsEur = (holdings ?? [])
    .filter((h) => h.value !== null && h.value !== undefined)
    .reduce((acc, h) => acc + Number(h.value), 0);

  const capturedAt = (balances ?? []).reduce<string | null>((latest, b) => {
    if (!latest) return b.captured_at;
    return new Date(b.captured_at) > new Date(latest) ? b.captured_at : latest;
  }, null);

  return {
    loading: balances === null,
    error,
    totalEur: accountsEur + holdingsEur,
    accountsEur,
    holdingsEur,
    capturedAt,
    hasData: balances !== null,
  };
}
